import { createReducer } from "typesafe-actions";

import DateRange from "@mapmycustomers/shared/types/range/DateRange";

import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import type { EfficiencyDrillDownStats } from "@app/store/dashboard/efficiencyTracker/fetchEfficiencyDrillDownStats";
import EfficiencyTrackerCardConfiguration from "@app/types/dashboard/cards/efficiencyTracker/EfficiencyTrackerCardConfiguration";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { createEmptyFilterModel } from "@app/util/filters/utils";

import {
  applyEfficiencyTrackerDrillDownListViewSettings,
  EfficiencyTrackerCardActions,
  fetchEfficiencyTrackerCardDrillDownData,
  hideEfficiencyTrackerDrillDown,
  setEfficiencyTrackerDrillDownStats,
  showEfficiencyTrackerDrillDown,
} from "./actions";

export interface EfficiencyTrackerCardState {
  configuration?: EfficiencyTrackerCardConfiguration; // also works as a "visible" trigger, visible if set
  drillDownCustomDateRange?: DateRange;
  loading?: boolean;
  stats?: EfficiencyDrillDownStats;
  totalFilteredRecords: number;
  totalRecords: number;
  viewState: DrillDownViewState;
}

export const efficiencyTrackerInitialState: EfficiencyTrackerCardState = {
  totalFilteredRecords: 0,
  totalRecords: 0,
  viewState: {
    columns: [],
    filter: createEmptyFilterModel(),
    range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
    sort: [],
  },
};

const efficiencyTrackerState = createReducer<
  EfficiencyTrackerCardState,
  EfficiencyTrackerCardActions
>(efficiencyTrackerInitialState)
  .handleAction(showEfficiencyTrackerDrillDown.request, (state, { payload }) => ({
    ...state,
    configuration: payload.configuration,
    drillDownCustomDateRange: payload.customDateRange,
  }))
  .handleAction(showEfficiencyTrackerDrillDown.success, (state, { payload }) => ({
    ...state,
    viewState: payload.viewState,
  }))
  .handleAction(setEfficiencyTrackerDrillDownStats, (state, { payload }) => ({
    ...state,
    stats: payload,
  }))
  .handleAction(hideEfficiencyTrackerDrillDown, (state) => ({
    ...state,
    configuration: undefined,
    drillDownCustomDateRange: undefined,
    stats: undefined,
  }))
  .handleAction(fetchEfficiencyTrackerCardDrillDownData.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(fetchEfficiencyTrackerCardDrillDownData.success, (state, { payload }) => ({
    ...state,
    loading: false,
    totalFilteredRecords: payload.totalFilteredRecords,
    totalRecords: payload.totalRecords,
  }))
  .handleAction(fetchEfficiencyTrackerCardDrillDownData.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(applyEfficiencyTrackerDrillDownListViewSettings, (state, { payload }) => ({
    ...state,
    viewState: {
      ...state.viewState,
      columns: payload.columns ?? state.viewState.columns,
      filter: payload.filter ?? state.viewState.filter,
      range: payload.range ?? state.viewState.range,
      sort: payload.sort ?? state.viewState.sort,
    },
  }));

export default efficiencyTrackerState;
